import { call, put, takeLatest } from "redux-saga/effects";
import * as Api from "../../api";
import * as actionConstants from "../../actions/partiesActionConstants";
import {
  fetchPartiesFailure,
  fetchPartiesSuccess,
  fetchPartiesRequest,
  deletePartyRequest,
  deletePartySuccess,
  deletePartyFailure,
  activateDeactivatePartyRequest,
  activateDeactivatePartySuccess,
  activateDeactivatePartyFailure,
} from "./partiesSlice";
import {SHOW_TOAST_WITH_PROPS} from "../../../../common/actionConstants"

// Extract parties data from API response
const extractPartiesData = (response = {}) => {
  if (response?.results && Array.isArray(response.results)) {
    return {
      results: response.results,
      total: response.total,
    };
  }
  
  // Fallback for array response
  if (Array.isArray(response)) {
    return { results: response };
  }
  
  // Fallback for wrapped response (shouldn't happen with interceptor)
  if (response?.data?.results && Array.isArray(response.data.results)) {
    return response.data;
  }
  
  return { results: [] };
};

// Extract total count from API response
const extractTotalCount = (response = {}, normalizedData = {}) => {
  const resultsLength = normalizedData.results?.length || 0;
  
  // Check response root level first (where it actually is)
  if (typeof response.total === "number") {
    return response.total;
  }
  
  // Fallback checks for wrapped responses
  if (typeof response.data?.total === "number") {
    return response.data.total;
  }
  
  // Last resort: use results length
  return resultsLength;
};

// Saga to fetch parties data
function* fetchPartiesSaga(action) {
  const params = action.payload || {};

  try {
    // Call API to fetch parties
    const response = yield call(
      Api.fetchPartiesData,
      actionConstants.FETCH_PARTIES_DATA,
      params,
    );
    
    const normalizedData = extractPartiesData(response);
    const totalCount = extractTotalCount(response, normalizedData);

    const successPayload = {
      data: normalizedData,
      params,
      metadata: {
        total: totalCount,
        page: params.page_number || 1,
        pageSize: params.page_size || 100,
      },
    };
    
    yield put(fetchPartiesSuccess(successPayload));
  } catch (error) {
    // Handle different error types
    const errorMessage = error?.response?.data?.message 
      || error?.message 
      || "Failed to fetch parties";

    yield put(fetchPartiesFailure(errorMessage));
  }
}

// Helper function for toast configuration
const getToastPayload = (message, type = "info") => ({
  message,
  type,
  delayTime: 5000,
  isCloseIcon: true,
  autoClose: true,
});

// Saga to delete a party by ID
function* deletePartySaga(action) {
  const { id } = action.payload;
  try {
    yield call(Api.deletePartyById, actionConstants.DELETE_PARTY, { id });
    yield put(deletePartySuccess({ id }));
    yield put({
      type: SHOW_TOAST_WITH_PROPS,
      payload: getToastPayload("Party deleted successfully."),
    });
  } catch (error) {
    console.error("Error in deletePartySaga:", error);
    yield put({
      type: SHOW_TOAST_WITH_PROPS,
      payload: getToastPayload("Failed to delete party."),
    });
    yield put(deletePartyFailure())
  }
}

// Saga to activate or deactivate a party
function* activateDeactivatePartySaga(action) {
  const { id, flag } = action.payload;
  try {
    yield call(Api.activateDeactivatePartyRequest, actionConstants.ACTIVATE_DEACTIVATE_PARTY, { id, flag });
    yield put(activateDeactivatePartySuccess({ id, flag }));
    yield put({
      type:SHOW_TOAST_WITH_PROPS,
      payload: getToastPayload("Party updated successfully."),
    })
  } catch (error) {
    console.error("Error in activateDeactivatePartySaga:", error);
    yield put({
      type: SHOW_TOAST_WITH_PROPS,
      payload: getToastPayload("Failed to update party status."),
    });
    yield put(activateDeactivatePartyFailure())
  }
}

// Listens for FETCH_PARTIES_REQUEST action and triggers fetchPartiesSaga
export default function* partiesSaga() {
  yield takeLatest(fetchPartiesRequest.type, fetchPartiesSaga);
  yield takeLatest(deletePartyRequest.type, deletePartySaga);
  yield takeLatest(activateDeactivatePartyRequest.type, activateDeactivatePartySaga);
}
